<template>
  <BasePageContainer class="note-templates">
    <SettingHeader>ノートテンプレートの管理</SettingHeader>
    <div class="pt-10 pb-11">
      <div class="note-templates__filter">
        <b-dropdown
          right
          variant="primary"
          size="sm"
          text="グループ選択"
          menu-class="note-templates__filter-menu"
          data-testid="note-template-department-filter">
          <b-dropdown-item-button
            :active="selectedDepartmentIds.length === 0"
            @click="clearFilter">
            すべて表示
          </b-dropdown-item-button>
          <b-dropdown-divider />
          <b-dropdown-item-button
            v-for="department in accessibleDepartments"
            :key="department.id"
            :active="isSelected(department.id)"
            @click="toggleDepartment(department.id)">
            {{ department.name }}
          </b-dropdown-item-button>
        </b-dropdown>
      </div>

      <ul class="note-templates__list list-unstyled">
        <NoteTemplateListItem
          v-for="noteTemplate in noteTemplates"
          :key="noteTemplate.id"
          :note-template="noteTemplate"
          :can-edit="canEdit(noteTemplate)"
          @edit="onClickEdit"
          @delete="onClickDelete" />
      </ul>
      <p
        v-if="initialized && noteTemplates.length === 0"
        class="note-templates__empty">
        テンプレートがありません
      </p>

      <FixedBottomRightArea>
        <IconButton
          icon="add"
          width="62"
          height="62"
          icon-size="30"
          data-testid="note-template-add-button"
          @click="$router.push('/admin/note_templates/add')" />
      </FixedBottomRightArea>
    </div>
  </BasePageContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import SettingHeader from '@/components/parts/SettingHeader.vue'
import IconButton from '@/components/parts/IconButton.vue'
import FixedBottomRightArea from '@/components/parts/FixedBottomRightArea.vue'
import NoteTemplateListItem from '@/components/view/note_templates/NoteTemplateListItem.vue'
import { canEditNoteTemplate, TEMPLATE_CHANGED_MESSAGE } from '@/helper/noteTemplateDepartments'

export default {
  name: 'NoteTemplates',
  components: {
    SettingHeader,
    IconButton,
    FixedBottomRightArea,
    NoteTemplateListItem
  },
  data() {
    return {
      noteTemplates: [],
      selectedDepartmentIds: [],
      initialized: false
    }
  },
  computed: {
    ...mapGetters('auth', ['accessibleDepartments'])
  },
  async created() {
    this.$store.commit('startLoading')
    await this.fetchNoteTemplates()
    this.$store.commit('finishLoading')
    this.initialized = true
  },
  methods: {
    canEdit(noteTemplate) {
      return canEditNoteTemplate(noteTemplate)
    },
    isSelected(id) {
      return this.selectedDepartmentIds.includes(id)
    },
    async clearFilter() {
      this.selectedDepartmentIds = []
      await this.reload()
    },
    async toggleDepartment(id) {
      this.selectedDepartmentIds = this.isSelected(id)
        ? this.selectedDepartmentIds.filter(departmentId => departmentId !== id)
        : [...this.selectedDepartmentIds, id]
      await this.reload()
    },
    async reload() {
      this.$store.commit('startLoading')
      await this.fetchNoteTemplates()
      this.$store.commit('finishLoading')
    },
    fetchNoteTemplates() {
      // 並び順は API 側で確定しているため、フロントでは並べ替えない
      // department__in に空文字を送るとフィルタなし扱いになるため、未選択時はパラメータ自体を送らない
      // ページングで取りこぼさないよう全件を一度に取得する
      const params = this.selectedDepartmentIds.length > 0
        ? { limit: 99999, department__in: this.selectedDepartmentIds.join(',') }
        : { limit: 99999 }
      return this.$axios.get('/note_templates/', { params })
        .then(res => {
          this.noteTemplates = res.data.results
        })
        .catch(() => {
          this.$store.commit('notify/set', { isUnknownError: true, type: 'danger' })
        })
    },
    onClickEdit(noteTemplate) {
      this.$router.push(`/admin/note_templates/${noteTemplate.id}/edit`)
    },
    async onClickDelete(noteTemplate) {
      // ボタンを隠すだけでは DOM 経由の実行を防げないため、ここでも判定する
      if (!this.canEdit(noteTemplate)) return
      if (!window.confirm(`テンプレート「${noteTemplate.name}」を削除します。既に作成されたノートの内容は変更されません。よろしいですか？`)) return

      this.$store.commit('startLoading')
      const deleted = await this.$axios.delete(`/note_templates/${noteTemplate.id}/`)
        .then(() => true)
        .catch(async e => {
          // 編集不能になったテンプレートの削除は API が 404 を返す（api の note_templates）。
          // 一覧を取り直せば editableStatus が更新されてボタンが消えるので、
          // 同じ操作を繰り返して失敗し続ける状態にしない
          if (e.response?.status === 404) {
            this.$store.commit('notify/set', { message: TEMPLATE_CHANGED_MESSAGE, type: 'danger' })
            await this.fetchNoteTemplates()
            return false
          }
          this.$store.commit('notify/set', { isUnknownError: true, type: 'danger' })
          return false
        })
      if (deleted) {
        this.$store.commit('notify/set', { message: 'テンプレートを削除しました。' })
        await this.fetchNoteTemplates()
      }
      this.$store.commit('finishLoading')
    }
  }
}
</script>

<style lang="scss" scoped>
.note-templates {
  &__filter {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  ::v-deep .note-templates__filter-menu {
    max-height: 240px;
    overflow-y: auto;
  }

  &__list {
    display: grid;
    gap: 12px;
    margin-bottom: 0;
  }

  &__empty {
    margin-top: 24px;
    text-align: center;
    // $grey10 は罫線用（line02）で本文には薄すぎるため、本文用の text02 を使う
    color: $grey08;
  }
}
</style>
